import {SagaIterator} from 'redux-saga';
import {all, call, put, putResolve, select} from 'redux-saga/effects';

import {AddressType} from 'desktop/types/addressType';
import {getAffId} from 'libs/analytics/affiliate';
import {apiConfig} from 'libs/api/api';
import {AmazonAddressDataType} from 'types/amazonType';

import {saveBillingAddress} from '../../../reducers/deliveryStep/billingAddress/billingAddress';
import {saveCustomerAddresses} from '../../../reducers/deliveryStep/customerAddresses/customerAddresses';
import {saveShippingAddress} from '../../../reducers/deliveryStep/shippingAddress/shippingAddress';
import {getBillingAddress} from '../../../selectors/billingAddress/billingAddress';
import {getCustomerAddresses} from '../../../selectors/deliveryStep/deliveryStep';
import {getShippingAddress} from '../../../selectors/shippingAddress/shippingAddress';

import {deleteAddressIDFromAddress} from './utils/deleteAddressIDFromAddress/deleteAddressIDFromAddress';
import {makeAmazonFormData} from './utils/makeAmazonFormData/makeAmazonFormData';
import {makeFormData} from './utils/makeFormData/makeFormData';

/**
 * Makes form data object
 * @param {AddressType} shipping - Received shipping address
 * @param {AddressType} billing - Received billing address
 * @return {SagaIterator<{formData: FormData; loggedInCustomerEmail: string}>} - Resulted FormData
 */
export function* getFormData(
    shipping: AddressType,
    billing: AddressType,
): SagaIterator<{formData: FormData; loggedInCustomerEmail: string}> {
    const hostState = apiConfig.hostStore.getState();
    const {email: customerEmail, form_key: formKey, is_logged_in: isLoggedIn} = hostState.customer;
    const {search} = hostState.router.location;

    const loggedInCustomerEmail = isLoggedIn ? customerEmail : '';

    const affId = getAffId({search});
    // Add shipping, billing address and affId to form
    const formData = yield call(
        makeFormData,
        {
            billing: {
                ...billing,
                email: {
                    ...billing.email,
                    value: loggedInCustomerEmail || billing.email.value,
                },
            },
            shipping: {
                ...shipping,
                email: {
                    ...shipping.email,
                    value: loggedInCustomerEmail || shipping.email.value,
                },
            },
        },
        formKey,
        affId,
    );

    return {
        formData,
        loggedInCustomerEmail,
    };
}

/**
 * Makes form data object
 * @param {AmazonAddressDataType} shipping - Received shipping address
 * @param {AmazonAddressDataType} billing - Received billing address
 * @return {SagaIterator<{formData: FormData}>} - Resulted FormData
 */
export function* getFormDataForAmazon(
    shipping: AmazonAddressDataType,
    billing: AmazonAddressDataType,
): SagaIterator<{formData: FormData}> {
    const hostState = apiConfig.hostStore.getState();
    const {email: customerEmail, form_key: formKey, is_logged_in: isLoggedIn} = hostState.customer;
    const {search} = hostState.router.location;

    const loggedInCustomerEmail = isLoggedIn ? customerEmail : '';

    const affId = getAffId({search});
    // Add shipping, billing address and affId to form
    const formData = yield call(
        makeAmazonFormData,
        {
            billing: {
                ...billing,
                email: loggedInCustomerEmail || billing.email,
            },
            shipping: {
                ...shipping,
                email: loggedInCustomerEmail || shipping.email,
            },
        },
        formKey,
        affId,
    );

    return {
        formData,
    };
}

/**
 * save processed address
 */
export function* updateAddress(processedAddress: {
    shippingAddress: AddressType;
    billingAddress: AddressType;
}) {
    yield put(saveShippingAddress(processedAddress.shippingAddress));
    yield put(saveBillingAddress(processedAddress.billingAddress));
}

/**
 * prepare address data
 * we have case:
 * some users can't move to step 2 (req `save` every time failed)
 * because they have addressID in localStorage when they be not logged
 * address data by addressID not match with data witch user was filled
 */
export function* prepareAddresses(): SagaIterator<{
    shippingAddress: AddressType;
    billingAddress: AddressType;
}> {
    const [shippingAddress, billingAddress, customerAddresses] = yield all([
        select(getShippingAddress),
        select(getBillingAddress),
        select(getCustomerAddresses),
    ]);

    const isUserHasAddresses =
        customerAddresses.billing_address_id && customerAddresses.shipping_address_id;
    const {is_logged_in: isLoggedIn} = apiConfig.hostStore.getState().customer;

    const isNeedCleanAddressID =
        !(isLoggedIn && isUserHasAddresses) &&
        (shippingAddress.addressID || billingAddress.addressID);

    if (isNeedCleanAddressID) {
        const processedAddress = yield call(
            deleteAddressIDFromAddress,
            shippingAddress,
            billingAddress,
        );
        yield call(updateAddress, processedAddress);
        return processedAddress;
    }

    return {
        billingAddress,
        shippingAddress,
    };
}

/**
 * Saves customer addresses
 */
export function* saveCustomerAddressSaga(): SagaIterator {
    const {billingAddress, shippingAddress} = yield call(prepareAddresses);

    const {
        email: {value: shippingEmail},
        phone: {value: shippingPhone},
    } = shippingAddress;

    const {formData, loggedInCustomerEmail} = yield call(
        getFormData,
        shippingAddress,
        billingAddress,
    );

    yield putResolve(saveCustomerAddresses(formData));

    apiConfig.hostStore.dispatch({
        payload: {
            customerEmail: shippingEmail || loggedInCustomerEmail,
            customerPhone: shippingPhone,
        },
        type: 'GET_CUSTOMER_FOR_REORDER',
    });
}

/**
 * Saves customer addresses
 */
export function* saveCustomerAddressSagaFromAmazon(
    shippingAddress: AmazonAddressDataType,
    billingAddress: AmazonAddressDataType,
): SagaIterator {
    const {formData} = yield call(getFormDataForAmazon, shippingAddress, billingAddress);

    yield putResolve(saveCustomerAddresses(formData));

    apiConfig.hostStore.dispatch({
        payload: {
            customerEmail: shippingAddress.email,
            customerPhone: shippingAddress.phone,
        },
        type: 'GET_CUSTOMER_FOR_REORDER',
    });
}
